// Multilingual printable invoice / quotation / receipt for SAS UNIVERS COSMETIC.
//
// Usage:
//   import { printTransaction } from '../lib/printDoc';
//   printTransaction(sale, { language: 'fr', t });
//
// `language` defaults to 'fr' (UNIVERS COSMETIC operates in France). It can be
// overridden per call to honour `customer.document_language` regardless of the
// UI language.
//
// Design: premium French B2B invoice — A4, Century Gothic, modular header
// (social + contact strip), client block with Client N°/SIRET/TVA, sales-
// conditions section, fixed legal footer on every page, "Page X of Y" counter,
// and Google-Review QR code anchored near the totals.

import { formatCurrency, formatDate, formatNumber, formatVat } from './formatters';
import { BRAND_ASSETS } from '../config/brandAssets';
import { openPrintWindow } from './openPrintWindow';

// =========================================================================
// Brand-level constants — edit these to rebrand without touching markup.
// =========================================================================
const LOGO_URL = BRAND_ASSETS.LOGO_PRIMARY;

// Custom-branded Google-Review QR (uploaded artwork with the UniCosm logo in
// the centre). Replace this URL to swap in a new QR design without touching
// markup. The QR encodes the Google Maps review URL for place id
// `ChIJnc07GuVs5kcRp4ktxElLtYo`.
const QR_CODE_URL = BRAND_ASSETS.REVIEW_QR;

const COMPANY = {
    facebook: '/UniCosm',
    instagram: '@UniCosm',
    phone: '+33 (0) 1 47 43 28 12',
    email: 'bonjour@universcosmetic.eu',
    website: 'www.universcosmetic.eu',
    tagline: 'PARTENAIRE DE CONFIANCE EN DISTRIBUTION BEAUTÉ',
    legal_line_1: 'SAS UNIVERS COSMETIC, 21 RUE EUGENE HENAFF, 93000 BOBIGNY, FRANCE.',
    legal_line_2:
        'SIEGE SOCIAL : 89302923100014 RCS BOBIGNY, TVA INTRACOM : FR85893029231, EORI N° FR89302923100014, APE : 4775Z',
    legal_line_3: 'IBAN : FR76 3000 3028 3900 0200 3387 119, BIC - SWIFT : SOGEFRPP',
};

const safe = (s) => (s == null ? '' : String(s));

// Inline SVGs (no font / emoji dependency). Stroke colour inherits via currentColor.
const ICON = {
    facebook: '<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.6 9.9V14.9H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12Z"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>',
    phone: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.7 2Z"/></svg>',
    email: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>',
    web: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>',
    fiveStars: '<svg viewBox="0 0 110 20" width="78" height="14" xmlns="http://www.w3.org/2000/svg"><g fill="#FBBC04"><polygon points="10,1 12.6,7.3 19.5,7.8 14.2,12.2 15.9,18.9 10,15.3 4.1,18.9 5.8,12.2 0.5,7.8 7.4,7.3"/><polygon points="32,1 34.6,7.3 41.5,7.8 36.2,12.2 37.9,18.9 32,15.3 26.1,18.9 27.8,12.2 22.5,7.8 29.4,7.3"/><polygon points="54,1 56.6,7.3 63.5,7.8 58.2,12.2 59.9,18.9 54,15.3 48.1,18.9 49.8,12.2 44.5,7.8 51.4,7.3"/><polygon points="76,1 78.6,7.3 85.5,7.8 80.2,12.2 81.9,18.9 76,15.3 70.1,18.9 71.8,12.2 66.5,7.8 73.4,7.3"/><polygon points="98,1 100.6,7.3 107.5,7.8 102.2,12.2 103.9,18.9 98,15.3 92.1,18.9 93.8,12.2 88.5,7.8 95.4,7.3"/></g></svg>',
};

// =========================================================================
// Fallback PDF translations (kept in sync with /public/locales/<lng>/invoicing.json -> pdf)
// =========================================================================
const PDF_DICT = {
    fr: {
        doc_title: { invoice: 'FACTURE', quotation: 'DEVIS', receipt: 'REÇU DE VENTE' },
        billed_to: 'Facturé\u00a0à',
        billed_to_quotation: 'Devis\u00a0à',
        issued_by: 'Émis par',
        line_items: 'Lignes de facture',
        headers: { row: 'N°', sku: 'Réf.', product: 'Produit', qty: 'Qté', unit_price: 'P.U. HT', vat: 'TVA', total: 'Total HT' },
        subtotal: 'Sous-total HT',
        discount: 'Remise',
        vat_line: (rate) => `TVA (${rate} %)`,
        delivery_label: 'Livraison (hors TVA)',
        total: 'Total TTC',
        total_amount: 'Montant total TTC',
        payment_breakdown: 'Détail du règlement',
        method: 'Mode',
        amount: 'Montant',
        cheques: 'Chèques',
        cheque: { bank: 'Banque', number: 'N° de chèque', deposit_date: 'Date de dépôt', amount: 'Montant', status: 'Statut' },
        notes: 'Observations',
        quotation_disclaimer: "Ce document n'est pas une facture acquittée de la TVA.",
        payment_terms:
            "Règlement à 30 jours date de facture. En cas de retard, application d'une indemnité forfaitaire de 40 € pour frais de recouvrement (art. L.441-10 du Code de commerce).",
        client_no_label: 'Client N°',
        siret_label: 'SIRET N°',
        vat_label: 'TVA N°',
        sales_conditions_title: 'Conditions générales de ventes :',
        sales_conditions_body:
            "Nos marchandises sont vendues et prises dans notre magasin. Toute réclamation portant sur la quantité, qualité, poids, prix, tare (celle-ci étant approximative) devra être faite dans les 24 heures. Le transfert des risques sur les produits a lieu dès la sortie des marchandises des entrepôts du vendeur. Il en résulte notamment que les marchandises voyagent aux risques et périls de l'acheteur.",
        quotation_validity_title: 'Validité du devis :',
        quotation_validity_body: 'Le prix mentionné dans ce devis est valable 07 jours.',
        google_review: 'Laissez-nous un avis Google',
        page_label: 'Page',
        of_label: 'of',
        thanks_footer: 'Merci pour votre confiance.',
        method_labels: { cash: 'Espèces', card: 'Carte bancaire', bank_transfer: 'Virement', cheque: 'Chèque', credit: 'Crédit', split: 'Multi-paiement' },
    },
    en: {
        doc_title: { invoice: 'INVOICE', quotation: 'QUOTATION', receipt: 'SALES RECEIPT' },
        billed_to: 'Billed To',
        billed_to_quotation: 'Quote To',
        issued_by: 'Issued By',
        line_items: 'Line Items',
        headers: { row: '#', sku: 'SKU', product: 'Product', qty: 'Qty', unit_price: 'Unit Price', vat: 'VAT', total: 'Total' },
        subtotal: 'Subtotal',
        discount: 'Discount',
        vat_line: (rate) => `VAT (${rate}%)`,
        delivery_label: 'Delivery (VAT-exempt)',
        total: 'Total',
        total_amount: 'Total Amount',
        payment_breakdown: 'Payment Breakdown',
        method: 'Method',
        amount: 'Amount',
        cheques: 'Cheques',
        cheque: { bank: 'Bank', number: 'Cheque #', deposit_date: 'Deposit Date', amount: 'Amount', status: 'Status' },
        notes: 'Notes',
        quotation_disclaimer: 'This document is not a paid VAT invoice.',
        payment_terms: 'Payment is due within 30 days of invoice date.',
        client_no_label: 'Client N°',
        siret_label: 'SIRET N°',
        vat_label: 'VAT N°',
        sales_conditions_title: 'General Sales Conditions:',
        sales_conditions_body:
            "Nos marchandises sont vendues et prises dans notre magasin. Toute réclamation portant sur la quantité, qualité, poids, prix, tare (celle-ci étant approximative) devra être faite dans les 24 heures. Le transfert des risques sur les produits a lieu dès la sortie des marchandises des entrepôts du vendeur. Il en résulte notamment que les marchandises voyagent aux risques et périls de l'acheteur.",
        quotation_validity_title: 'Quotation Validity:',
        quotation_validity_body: 'Le prix mentionné dans ce devis est valable 07 jours.',
        google_review: 'Laissez-nous un avis Google',
        page_label: 'Page',
        of_label: 'of',
        thanks_footer: 'Merci pour votre confiance.',
        method_labels: { cash: 'Cash', card: 'Card', bank_transfer: 'Bank Transfer', cheque: 'Cheque', credit: 'Credit', split: 'Split' },
    },
};

const pickDict = (language) => PDF_DICT[(language || '').slice(0, 2)] || PDF_DICT.fr;

export function buildInvoiceHtml(tx, options = {}) {
    if (!tx) return '';
    const language = options.language || 'fr';
    const D = pickDict(language);

    const fmtMoney = (n) => formatCurrency(n, language);
    const fmtNum = (n) => formatNumber(n, language, 2);
    const fmtWhen = (iso) => formatDate(iso, language);

    const isQuotation = tx.status === 'quotation';
    const isDraft = tx.status === 'draft';
    // Draft + Quotation share the "not a paid VAT invoice" disclaimer since
    // neither has been finalized. Restored Jul 2026 after a template refactor
    // narrowed the flag to quotations only.
    const isDraftOrQuote = isDraft || isQuotation;
    const isInvoice = (tx.type || 'sale') === 'invoice';
    const docKey = isQuotation ? 'quotation' : isInvoice ? 'invoice' : 'receipt';
    const docTitle = D.doc_title[docKey];

    // Show per-line VAT column only when lines have differing rates
    const lineRates = (tx.line_items || [])
        .map((li) => (li.vat_rate != null ? Number(li.vat_rate) : null))
        .filter((r) => r != null);
    const showLineVat = new Set(lineRates).size > 1;

    const translateMethod = (m) => D.method_labels[m] || safe(m).replace('_', ' ');

    const lineItemRows = (tx.line_items || [])
        .map(
            (li, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${safe(li.sku)}</td>
          <td>${safe(li.product_name)}</td>
          <td class="num">${fmtNum(li.quantity)}</td>
          <td class="num">${fmtMoney(li.unit_price)}</td>
          ${showLineVat ? `<td class="num">${formatVat(li.vat_rate ?? 0, language)}</td>` : ''}
          <td class="num">${fmtMoney(li.line_total)}</td>
        </tr>`
        )
        .join('');

    const paymentRows = (tx.payments && tx.payments.length
        ? tx.payments
        : [{ method: tx.payment_method, amount: tx.amount }]
    )
        .map(
            (p) => `
        <tr>
          <td>${translateMethod(p.method)}</td>
          <td class="num">${fmtMoney(p.amount)}</td>
        </tr>`
        )
        .join('');

    const chequeRows = (tx.cheques || [])
        .map(
            (c) => `
        <tr>
          <td>${safe(c.bank_name)}</td>
          <td>${safe(c.cheque_number)}</td>
          <td>${safe(c.deposit_date)}</td>
          <td class="num">${fmtMoney(c.amount)}</td>
          <td style="text-transform:capitalize;">${safe(c.status)}</td>
        </tr>`
        )
        .join('');

    const html = `<!DOCTYPE html>
<html lang="${language}">
<head>
<meta charset="utf-8" />
<title>${docTitle} ${safe(tx.invoice_number)}</title>
<style>
  /* ============================================================
     TRUE PAGE-MARGIN LETTERHEAD — header & legal footer in page-margin
     bands via position:fixed. Body content stays out of those bands
     thanks to a transparent <thead>/<tfoot> spacer table that Chromium
     auto-repeats on every printed page.

       ┌──────────────────────────────────────┐
       │ HEADER (fixed top — every page)      │
       ├──────────────────────────────────────┤
       │                                      │
       │         DOCUMENT BODY                │
       │                                      │
       ├──────────────────────────────────────┤
       │ LEGAL FOOTER (fixed bottom — every)  │
       │ Page X of Y         Merci ...        │  ← @page bottom counter
       └──────────────────────────────────────┘
     ============================================================ */
  @page {
    size: A4;
    /* Tiny 10mm bottom reserve so the @bottom-left page counter and
       @bottom-right "Merci…" tag have somewhere to render BELOW the
       fixed legal footer band. */
    margin: 0 0 10mm 0;
    @bottom-left {
      content: "${D.page_label} " counter(page) " ${D.of_label} " counter(pages);
      font-family: 'Century Gothic', 'CenturyGothic', 'Manrope', 'DM Sans', sans-serif;
      font-size: 9px;
      font-style: italic;
      color: #888;
      margin-left: 12mm;
    }
    @bottom-right {
      content: "${D.thanks_footer}";
      font-family: 'Century Gothic', 'CenturyGothic', 'Manrope', 'DM Sans', sans-serif;
      font-size: 9px;
      color: #C08478;
      font-style: italic;
      margin-right: 12mm;
    }
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Century Gothic', 'CenturyGothic', 'Manrope', 'DM Sans', Arial, sans-serif;
    color: #1A1A1A;
    font-size: 11px;
    line-height: 1.45;
    -webkit-font-smoothing: antialiased;
  }

  /* Per-page spacer table — repeats <thead>/<tfoot> automatically. */
  table.page-frame { width: 100%; border-collapse: collapse; }
  table.page-frame > thead { display: table-header-group; }
  table.page-frame > tfoot { display: table-footer-group; }
  table.page-frame > thead > tr > td.spacer {
    height: 42mm;
    padding: 0; border: 0; background: transparent;
  }
  table.page-frame > tfoot > tr > td.spacer {
    height: 24mm;
    padding: 0; border: 0; background: transparent;
  }
  table.page-frame > tbody > tr > td {
    padding: 0 12mm; border: 0;
  }

  .doc {
    padding: 4mm 4px 0 4px;
    max-width: 900px;
    margin: 0 auto;
  }

  /* ============ FIXED PAGE-MARGIN HEADER (repeats on every page) ============ */
  .page-header {
    position: fixed;
    top: 0; left: 0; right: 0;
    height: 42mm;
    padding: 8mm 12mm 0 12mm;
    background: #fff;
    z-index: 10;
  }
  .page-header > .inner { display: block; }
  .page-header .hdr-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding: 0 0 10px 0;
    border-bottom: 1.5px solid #1A1A1A;
  }
  .page-header .info-strip {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 18px;
    padding: 7px 0 6px 0;
    border-bottom: 1px solid #EFE5E1;
    color: #555;
    font-size: 9.5px;
  }
  .page-header .info-strip .group { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .page-header .info-strip .item { display: inline-flex; align-items: center; gap: 5px; color: #555; white-space: nowrap; }
  .page-header .info-strip .item svg { color: #C08478; flex: 0 0 auto; }
  .page-header .running-quote-banner {
    margin-top: 6px;
    padding: 4px 8px;
    border: 1px dashed #8B5CF6;
    color: #8B5CF6;
    font-style: italic;
    font-weight: 700;
    text-align: center;
    font-size: 10px;
  }

  /* ===================== HEADER ===================== */
  .hdr-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding-bottom: 14px;
    border-bottom: 1.5px solid #1A1A1A;
  }
  .brand { display: flex; align-items: center; gap: 14px; }
  .brand img { width: 62px; height: 62px; object-fit: contain; }
  .brand h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 2px;
    line-height: 1;
    display: inline-block;     /* shrink-wraps to its own intrinsic width */
  }
  .brand h1 span { font-weight: 300; letter-spacing: 4px; }
  .brand .brand-text { display: inline-block; }
  .brand .tagline {
    color: #888;
    font-size: 7.5px;
    letter-spacing: 0.6px;
    margin-top: 6px;
    text-transform: uppercase;
    /* Match the visual width of the "UNIVERS COSMETIC" title above */
    max-width: 235px;
    line-height: 1.45;
    text-align: center;
  }

  .doc-meta { text-align: right; min-width: 200px; }
  .doc-meta .title {
    font-size: 26px;
    letter-spacing: 6px;
    color: #C08478;
    font-weight: 600;
    line-height: 1;
  }
  .doc-meta .invno {
    font-family: 'Courier New', monospace;
    font-weight: 700;
    font-size: 13px;
    margin-top: 6px;
    color: #1A1A1A;
  }
  .doc-meta .date {
    color: #888;
    font-size: 10px;
    margin-top: 2px;
  }

  /* Social + Contact strip */
  .info-strip {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 18px;
    padding: 9px 0 14px 0;
    border-bottom: 1px solid #EFE5E1;
    color: #555;
    font-size: 10px;
  }
  .info-strip .group {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }
  .info-strip .item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #555;
    white-space: nowrap;
  }
  .info-strip .item svg { color: #C08478; flex: 0 0 auto; }

  /* ===================== CLIENT BLOCK ===================== */
  .client-row {
    display: flex;
    gap: 24px;
    margin-top: 18px;
  }
  .client-row > div { flex: 1; }
  .label {
    font-size: 9px;
    color: #C08478;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 4px;
    font-weight: 600;
    white-space: nowrap;
  }
  .client-name {
    font-size: 13px;
    font-weight: 700;
    color: #1A1A1A;
    margin-bottom: 3px;
  }
  .client-row .meta-line { color: #444; font-size: 10.5px; margin-top: 1px; }
  .id-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px 12px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #E5DAD5;
  }
  .id-grid .cell {
    font-size: 9.5px;
  }
  .id-grid .cell .k {
    color: #888;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 8.5px;
    display: block;
    margin-bottom: 1px;
  }
  .id-grid .cell .v {
    color: #1A1A1A;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 10px;
  }

  /* ===================== LINE ITEMS TABLE ===================== */
  .section { margin-top: 22px; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  thead { display: table-header-group; }
  th {
    background: #1A1A1A;
    color: #fff;
    text-align: left;
    padding: 8px 9px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  th.num { text-align: right; }
  td {
    padding: 9px;
    border-bottom: 1px solid #EFE5E1;
    font-size: 11px;
    vertical-align: top;
  }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr { page-break-inside: avoid; }
  tbody tr:nth-child(even) td { background: #FBF8F6; }

  /* ===================== TOTALS + QR ===================== */
  .totals-wrap {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 14px;
    gap: 24px;
  }
  .qr-block {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #EFE5E1;
    border-radius: 8px;
    background: #FBF8F6;
    max-width: 230px;
  }
  .qr-block img { width: 68px; height: 68px; object-fit: contain; }
  .qr-block .qr-cap {
    font-size: 10px;
    color: #444;
    line-height: 1.35;
  }
  .qr-block .qr-cap b { display: block; font-size: 10.5px; color: #1A1A1A; margin-bottom: 4px; }
  .qr-block .qr-cap .qr-stars { display: inline-block; line-height: 0; }

  .totals {
    width: 320px;
    margin-left: auto;
  }
  .totals .line {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 11px;
  }
  .totals .line .k { color: #555; }
  .totals .grand {
    margin-top: 6px;
    padding: 10px 12px;
    background: #1A1A1A;
    color: #fff;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }
  .totals .grand .k { color: rgba(255,255,255,0.7); font-weight: 500; }

  /* ===================== SALES CONDITIONS ===================== */
  /* Visual parity with .payment-terms — same padding, font, colour, border. */
  .conditions {
    margin-top: 14px;
    padding: 10px 12px;
    background: #F9F4F2;
    border-left: 3px solid #C08478;
    font-size: 10px;
    color: #555;
    line-height: 1.5;
  }
  .conditions .title { font-weight: 700; color: #1A1A1A; }

  /* ===================== PAYMENT TERMS ===================== */
  .payment-terms {
    margin-top: 14px;
    padding: 10px 12px;
    background: #F9F4F2;
    border-left: 3px solid #C08478;
    font-size: 10px;
    color: #555;
  }

  /* ===================== FIXED PAGE-MARGIN LEGAL FOOTER (repeats on every page) ===================== */
  .legal-footer {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    height: 22mm;
    padding: 5mm 12mm 3mm 12mm;
    border-top: 1.5px solid #1A1A1A;
    background: #fff;
    font-size: 8.5px;
    line-height: 1.55;
    color: #444;
    text-align: center;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    z-index: 10;
  }
  .legal-footer .l1 { font-weight: 700; color: #1A1A1A; }
  .legal-footer .l2, .legal-footer .l3 { color: #555; }

  /* ===================== PRINT TUNING ===================== */
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none !important; }
  }
</style>
</head>
<body onload="window.print()">

<!-- FIXED LETTERHEAD: rendered in the page-margin top region on every page.
     Body content (.doc) starts BELOW this band thanks to body.padding-top. -->
<header class="page-header">
  <div class="hdr-top">
    <div class="brand">
      <img src="${LOGO_URL}" alt="UNIVERS COSMETIC" />
      <div class="brand-text">
        <h1>UNIVERS <span>COSMETIC</span></h1>
        <div class="tagline">${COMPANY.tagline}</div>
      </div>
    </div>
    <div class="doc-meta">
      <div class="title">${docTitle}</div>
      <div class="invno">${safe(tx.invoice_number)}</div>
      <div class="date">${fmtWhen(tx.created_at)}</div>
    </div>
  </div>

  <div class="info-strip">
    <div class="group">
      <span class="item">${ICON.facebook}<span>${COMPANY.facebook}</span></span>
      <span class="item">${ICON.instagram}<span>${COMPANY.instagram}</span></span>
    </div>
    <div class="group">
      <span class="item">${ICON.phone}<span>${COMPANY.phone}</span></span>
      <span class="item">${ICON.email}<span>${COMPANY.email}</span></span>
      <span class="item">${ICON.web}<span>${COMPANY.website}</span></span>
    </div>
  </div>

  ${isDraftOrQuote ? `<div class="running-quote-banner">${D.quotation_disclaimer}</div>` : ''}
</header>

<!-- BODY: wrapped in a page-frame table so the <thead>/<tfoot> spacer
     rows auto-repeat on every printed page, keeping the body content
     out of the fixed header / footer bands. -->
<table class="page-frame">
<thead><tr><td class="spacer"></td></tr></thead>
<tfoot><tr><td class="spacer"></td></tr></tfoot>
<tbody><tr><td>
<div class="doc">

  <!-- ========= CLIENT BLOCK ========= -->
  <div class="client-row">
    <div>
      <div class="label">${isQuotation ? D.billed_to_quotation : D.billed_to}</div>
      <div class="client-name">${safe(tx.company_name) || '—'}</div>
      ${tx.company_address ? `<div class="meta-line">${safe(tx.company_address)}</div>` : ''}
      ${tx.contact_person ? `<div class="meta-line">${safe(tx.contact_person)}</div>` : ''}
      ${(tx.mobile_number || tx.email_address) ? `<div class="meta-line">${safe(tx.mobile_number)}${tx.mobile_number && tx.email_address ? ' · ' : ''}${safe(tx.email_address)}</div>` : ''}
      <div class="id-grid">
        <div class="cell"><span class="k">${D.client_no_label}</span><span class="v">${safe(tx.customer_code) || '—'}</span></div>
        <div class="cell"><span class="k">${D.siret_label}</span><span class="v">${safe(tx.siret) || '—'}</span></div>
        <div class="cell"><span class="k">${D.vat_label}</span><span class="v">${safe(tx.tva) || '—'}</span></div>
      </div>
    </div>
    <div style="text-align:right; min-width: 160px;">
      <div class="label">${D.issued_by}</div>
      <div class="client-name" style="font-size:11.5px;">${safe(tx.created_by_name) || '—'}</div>
      <div class="meta-line" style="color:#888;">${fmtWhen(tx.created_at)}</div>
    </div>
  </div>

  ${
      (isInvoice || isQuotation) && tx.line_items && tx.line_items.length
          ? `
  <div class="section">
    <div class="label">${D.line_items}</div>
    <table>
      <thead>
        <tr>
          <th>${D.headers.row}</th>
          <th>${D.headers.sku}</th>
          <th>${D.headers.product}</th>
          <th class="num">${D.headers.qty}</th>
          <th class="num">${D.headers.unit_price}</th>
          ${showLineVat ? `<th class="num">${D.headers.vat}</th>` : ''}
          <th class="num">${D.headers.total}</th>
        </tr>
      </thead>
      <tbody>${lineItemRows}</tbody>
    </table>

    <div class="totals-wrap">
      <div class="qr-block">
        <img src="${QR_CODE_URL}" alt="Google Review QR" />
        <div class="qr-cap">
          <b>${D.google_review}</b>
          <span class="qr-stars">${ICON.fiveStars}</span>
        </div>
      </div>
      <div class="totals">
        <div class="line"><span class="k">${D.subtotal}</span><span>${fmtMoney(tx.subtotal)}</span></div>
        ${tx.discount_amount && tx.discount_amount > 0 ? `
        <div class="line" style="color:#E76F51;"><span class="k">${D.discount}${tx.discount_type === 'percent' ? ` (${formatVat(tx.discount_value, language)})` : ''}</span><span>- ${fmtMoney(tx.discount_amount)}</span></div>
        ` : ''}
        ${(() => {
            const breakdown = tx.vat_breakdown && Object.keys(tx.vat_breakdown).length
                ? tx.vat_breakdown
                : (tx.vat_amount ? { [tx.vat_rate || 0]: tx.vat_amount } : {});
            return Object.entries(breakdown).map(([rate, amt]) =>
                `<div class="line"><span class="k">${D.vat_line(rate)}</span><span>${fmtMoney(amt)}</span></div>`
            ).join('');
        })()}
        ${tx.delivery_charge && tx.delivery_charge > 0 ? `
        <div class="line"><span class="k">${D.delivery_label}</span><span>${fmtMoney(tx.delivery_charge)}</span></div>
        ` : ''}
        <div class="line grand"><span class="k">${D.total}</span><span>${fmtMoney(tx.amount)}</span></div>
      </div>
    </div>
  </div>
  `
          : `
  <div class="section">
    <div class="totals-wrap">
      <div class="qr-block">
        <img src="${QR_CODE_URL}" alt="Google Review QR" />
        <div class="qr-cap">
          <b>${D.google_review}</b>
          <span class="qr-stars">${ICON.fiveStars}</span>
        </div>
      </div>
      <div class="totals">
        <div class="line grand"><span class="k">${D.total_amount}</span><span>${fmtMoney(tx.amount)}</span></div>
      </div>
    </div>
  </div>
  `
  }

  ${
      !isQuotation
          ? `
  <div class="section">
    <div class="label">${D.payment_breakdown}</div>
    <table>
      <thead><tr><th>${D.method}</th><th class="num">${D.amount}</th></tr></thead>
      <tbody>${paymentRows}</tbody>
    </table>
  </div>
  `
          : ''
  }

  ${
      chequeRows
          ? `
  <div class="section">
    <div class="label">${D.cheques}</div>
    <table>
      <thead><tr><th>${D.cheque.bank}</th><th>${D.cheque.number}</th><th>${D.cheque.deposit_date}</th><th class="num">${D.cheque.amount}</th><th>${D.cheque.status}</th></tr></thead>
      <tbody>${chequeRows}</tbody>
    </table>
  </div>
  `
          : ''
  }

  ${tx.notes ? `<div class="section"><div class="label">${D.notes}</div><div>${safe(tx.notes)}</div></div>` : ''}

  ${isInvoice && !isQuotation ? `<div class="payment-terms">${D.payment_terms}</div>` : ''}

  <!-- ========= SALES CONDITIONS (same format as payment-terms) ========= -->
  <div class="conditions">
    <span class="title">${D.sales_conditions_title}</span> ${D.sales_conditions_body}
  </div>

  ${isQuotation ? `
  <!-- ========= QUOTATION VALIDITY (only for devis) ========= -->
  <div class="conditions">
    <span class="title">${D.quotation_validity_title}</span> ${D.quotation_validity_body}
  </div>
  ` : ''}

</div><!-- /.doc -->
</td></tr></tbody>
</table>

<!-- FIXED LEGAL FOOTER: rendered in the page-margin bottom region on every page. -->
<footer class="legal-footer">
  <div class="l1">${COMPANY.legal_line_1}</div>
  <div class="l2">${COMPANY.legal_line_2}</div>
  <div class="l3">${COMPANY.legal_line_3}</div>
</footer>

</body>
</html>`;

    return html;
}

export function printTransaction(tx, options = {}) {
    const html = buildInvoiceHtml(tx, options);
    if (!html) return;
    const w = openPrintWindow(html, { width: 900, height: 1000 });
    if (!w) return;
}
